import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import NotifyButton from "@/components/NotifyButton";

/**
 * Shared scaffold for the four new section pages (Journal, Training Cards,
 * Insights, Books) while their real content is still being written.
 *
 * Every label/title pulls from i18n so the page is 100% bilingual.
 * Renders:
 *   • Cinzel inscriptional title
 *   • Italic gold subtitle (1–3 lines)
 *   • Intro paragraph
 *   • Optional categories/extras (children)
 *   • "Coming soon" note
 *   • Quiet email-notify CTA
 *   • Internal links back to the rest of the Stoa
 */
export default function SectionPlaceholder({
  eyebrow,
  title,
  subtitleLines = [],
  intro,
  comingSoon,
  children,
  source, // analytics tag, e.g. "journal" | "cards"
  otherSections = [],
}) {
  const { t } = useT();

  return (
    <main data-testid={`section-${source}`} className="relative pt-32 md:pt-40 pb-32">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        {/* breadcrumb / back */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors mb-12"
          data-testid={`section-${source}-back`}
        >
          <ArrowLeft size={12} strokeWidth={1.5} /> StoicOs · Virtue Guide
        </Link>

        {/* eyebrow */}
        <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{eyebrow}</p>

        {/* inscriptional title */}
        <h1
          data-testid={`section-${source}-title`}
          className="font-inscription text-[2.25rem] sm:text-5xl md:text-6xl text-bone tracking-[0.06em] uppercase leading-[1.05]"
        >
          {title}
        </h1>

        {/* italic gold subtitle (stacked) */}
        {subtitleLines.length > 0 && (
          <div className="mt-8 space-y-1">
            {subtitleLines.map((line) => (
              <p key={line} className="font-serif italic text-2xl md:text-3xl text-gold tracking-tight leading-tight">
                {line}
              </p>
            ))}
          </div>
        )}

        {/* intro */}
        {intro && (
          <p className="mt-10 text-bone/85 font-reading text-lg md:text-xl leading-[1.85] max-w-2xl">
            {intro}
          </p>
        )}

        {/* gold rule */}
        <div className="gold-rule w-40 mt-14" />

        {/* page-specific content (categories, featured article teaser, etc.) */}
        {children && <div className="mt-14">{children}</div>}

        {/* coming soon panel */}
        {comingSoon && (
          <section
            data-testid={`section-${source}-coming-soon`}
            className="mt-20 p-8 md:p-10 border border-gold/20 bg-ink-elevated/40"
          >
            <p className="text-[11px] tracking-[0.28em] uppercase text-gold mb-4 flex items-center gap-2">
              <Mail size={14} strokeWidth={1.5} /> {t("placeholder.notifyTitle")}
            </p>
            <p className="text-bone/85 font-reading text-lg leading-[1.8] max-w-xl">
              {comingSoon}
            </p>
            <p className="mt-4 text-fog font-light leading-relaxed max-w-xl">
              {t("placeholder.notifyBody")}
            </p>
            <div className="mt-6">
              <NotifyButton
                store="both"
                source={`placeholder_${source}`}
                label={t("placeholder.notifyButton")}
                testid={`section-${source}-notify-btn`}
              />
            </div>
          </section>
        )}

        {/* internal links to sibling sections */}
        {otherSections.length > 0 && (
          <section className="mt-24" data-testid={`section-${source}-explore`}>
            <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">
              {t("placeholder.exploreOther")}
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {otherSections.map(({ to, labelKey, testid }) => (
                <li key={to}>
                  <Link
                    to={to}
                    data-testid={testid}
                    className="group flex items-center justify-between gap-4 p-5 border border-white/8 hover:border-gold/40 hover:bg-white/[0.02] transition-all duration-300"
                  >
                    <span className="font-serif text-xl text-bone group-hover:text-gold transition-colors">
                      {t(labelKey)}
                    </span>
                    <ArrowRight size={14} strokeWidth={1.5} className="text-gold opacity-70 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </main>
  );
}
